import { TRow } from 'components/Kizen/Table';
import BasicTable from 'components/Tables/Basic';
import TablePagination from 'components/Tables/Big/TablePagination';
import useAsyncFnKeepLast from 'hooks/useAsyncFnKeepLast';
import { usePaginationSettings } from 'hooks/usePaginationSettings';
import { useEffect, useRef } from 'react';
import PerformActionService from 'services/PerformActionService';
import { useColumns } from './useColumns';
import {
  StyledCardToolbar,
  StyledPaginationWrapper,
  StyledTableWrapper,
} from './styles';
import { Gradient } from 'components/Kizen/ScrollFadeContainer';
import { EMPTY_ARRAY, EMPTY_OBJECT } from 'utility/fieldHelpers';
import { CardToolbarTitle } from 'components/Layout/CardToolbar';
import { useTranslation } from 'react-i18next';
import TableScrollContainer from 'components/Tables/ScrollContainer';
import { EXPORT_IMPORT_SOURCES } from './constants';
import Button from 'components/Button';
import { history } from 'routes';

const paginationSettings = { page: 1, pageSize: 25, search: '' };

const Table = ({ action, heading }) => {
  const { t } = useTranslation();

  const { page, pageSize, handleChangePage, handleChangePageSize, headData } =
    usePaginationSettings(paginationSettings);

  const resultsMapRef = useRef(null);
  const needReload = useRef(false);

  const [
    { value: { results = EMPTY_ARRAY, count } = EMPTY_OBJECT, loading },
    refetch,
  ] = useAsyncFnKeepLast(async () => {
    const response = await PerformActionService.getBulkActionProgress({
      action,
      page,
      page_size: pageSize,
    });

    if (action === EXPORT_IMPORT_SOURCES.IMPORT) {
      if (resultsMapRef.current) {
        response.results.forEach((result) => {
          // we want to trigger reload if an import has completed since the last fetch,
          // or if there are new imports that have completed since the last fetch
          if (result.status === 'completed') {
            const wasIncompleted = resultsMapRef.current.get(result.id);
            const isNewCompletedImport =
              page === 1 && !resultsMapRef.current.has(result.id);

            if (wasIncompleted || isNewCompletedImport) {
              needReload.current = true;
            }
          }
        });
      } else {
        resultsMapRef.current = new Map();
      }
      response.results.forEach((result) => {
        resultsMapRef.current.set(result.id, result.status !== 'completed');
      });
    }

    return response;
  }, [page, pageSize, action]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (action === EXPORT_IMPORT_SOURCES.IMPORT) {
      return history.listen(() => {
        if (needReload.current) {
          window.location.reload();
        }
      });
    }
  }, [action]);

  const columns = useColumns(action);

  return (
    <>
      <StyledCardToolbar>
        <CardToolbarTitle>{heading}</CardToolbarTitle>
        <Button variant="text" color="blue" onClick={refetch}>
          {t('Refresh')}
        </Button>
      </StyledCardToolbar>
      <StyledTableWrapper>
        <TableScrollContainer bottom top gradient={<Gradient />}>
          <BasicTable
            stickyHeader
            head={<TRow head columns={columns} data={headData} />}
            staleData={loading}
          >
            {results.map((data) => (
              <TRow key={data.id} columns={columns} data={data} />
            ))}
          </BasicTable>
        </TableScrollContainer>
      </StyledTableWrapper>
      <StyledPaginationWrapper>
        <TablePagination
          page={page}
          perPage={pageSize}
          totalCount={count}
          onChangePage={handleChangePage}
          onChangePerPage={handleChangePageSize}
          small
        />
      </StyledPaginationWrapper>
    </>
  );
};

export const ExportImportTables = () => {
  const { t } = useTranslation();

  return (
    <>
      <Table
        action={EXPORT_IMPORT_SOURCES.IMPORT}
        heading={t('Recent Imports')}
      />
      <Table
        action={EXPORT_IMPORT_SOURCES.EXPORT}
        heading={t('Recent Exports')}
      />
    </>
  );
};
